// src/utils/captcha.js - Reusable captcha utilities

/**
 * Initializes and loads the reCAPTCHA script
 * 
 * @param {Function} onTokenChange - Callback function when captcha token changes
 * @param {Function} onError - Callback function when captcha error occurs
 * @returns {Function} cleanup function to remove global callbacks
 */
export const initializeCaptcha = (onTokenChange, onError) => {
  // Define a more reliable way to check if recaptcha is already available
  const recaptchaLoaded = window.grecaptcha && window.grecaptcha.render;
  
  // Define the global callback functions for reCAPTCHA
  window.onCaptchaComplete = (token) => {
    if (onTokenChange) onTokenChange(token);
  };
  
  window.onCaptchaError = () => {
    if (onTokenChange) onTokenChange('');
    if (onError) onError('reCAPTCHA error occurred. Please try again.');
  };

  // Add global callback for when recaptcha is loaded
  window.onRecaptchaLoaded = () => {
    renderCaptcha();
  };
  
  // Function to render the captcha
  function renderCaptcha() {
    // Use a reliable approach with retry mechanism
    const maxRetries = 5;
    let retryCount = 0;

    const attemptRender = () => {
      if (window.grecaptcha && window.grecaptcha.render) {
        try {
          const captchaContainer = document.getElementById('g-recaptcha-container');
          if (captchaContainer) {
            // Check if this container already has a recaptcha instance
            if (!captchaContainer.hasAttribute('data-widget-id')) {
              const widgetId = window.grecaptcha.render('g-recaptcha-container', {
                sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI",
                callback: 'onCaptchaComplete',
                'error-callback': 'onCaptchaError'
              });
              // Store the widget ID on the element for future reference
              captchaContainer.setAttribute('data-widget-id', widgetId);
            }
          }
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
          // If the error is because the container is already rendered, that's okay
          if (!error.message.includes('already been rendered')) {
            // Retry a few times with exponential backoff
            if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(attemptRender, 500 * retryCount);
            }
          }
        }
      } else if (retryCount < maxRetries) {
        // If grecaptcha isn't available yet, retry
        retryCount++;
        setTimeout(attemptRender, 500 * retryCount);
      }
    };

    // Start the rendering process
    attemptRender();
  }
  
  // Only load the script if recaptcha isn't already available
  if (!recaptchaLoaded) {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoaded&render=explicit';
    script.async = true;
    script.defer = true;
    script.id = 'recaptcha-script';
    document.head.appendChild(script);
  } else {
    // If recaptcha is already available, try to render immediately
    renderCaptcha();
  }
  
  // Return cleanup function
  return () => {
    // Clean up global callbacks
    delete window.onCaptchaComplete;
    delete window.onCaptchaError;
    delete window.onRecaptchaLoaded;
  };
};

/**
 * Resets the captcha widget
 */
export const resetCaptcha = () => {
  if (window.grecaptcha) {
    try {
      const captchaContainer = document.getElementById('g-recaptcha-container');
      if (captchaContainer && captchaContainer.hasAttribute('data-widget-id')) {
        const widgetId = captchaContainer.getAttribute('data-widget-id');
        window.grecaptcha.reset(widgetId);
      } else {
        window.grecaptcha.reset();
      }
      return true;
    } catch (error) {
      console.error('Error resetting reCAPTCHA:', error);
      return false;
    }
  }
  return false;
};

/**
 * Creates a Captcha container component for use in forms
 */
export const CaptchaContainer = () => {
  return (
    <div className="captcha-container">
      <div 
        id="g-recaptcha-container"
        className="g-recaptcha" 
        data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"} 
        data-callback="onCaptchaComplete"
        data-error-callback="onCaptchaError"
      ></div>
    </div>
  );
}; 